import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

/**
 * 参数示例:
 * ```typescript
 * {
 *   "effect_scene": "hug",
 *   "input": {
 *     "images": [
 *       "https://example.com/person.jpg"
 *     ],
 *     "duration": 5
 *   }
 * }
 * ```
 */
export interface KlingEffectsSingleParams {
  /**
   * 特效场景，必填参数。支持100多种单人互动特效，包括: baseball（棒球）, inner_voice（内心独白）, a_list_look（名人效应）, memory_alive（记忆复活）, trampoline（蹦床）, trampoline_night（夜间蹦床）, pucker_up（嘟嘴）, guess_what（猜猜看）, feed_mooncake（喂月饼）, rampage_ape（暴走猿）, flyer（传单）, dishwasher（洗碗机）, pet_chinese_opera（宠物戏曲）, magic_fireball（魔法火球）, gallery_ring（画廊戒指）, pet_moto_rider（宠物骑士）, muscle_pet（肌肉宠物）, squeeze_scream（挤压尖叫）, pet_delivery（宠物快递）, running_man（奔跑吧）, disappear（消失）, mythic_style（神话风格）, steampunk（蒸汽朋克）, c4d_cartoon（C4D卡通）, 3d_cartoon_1（3D卡通1）, 3d_cartoon_2（3D卡通2）, eagle_snatch（鹰抓）, hug_from_past（来自过去的拥抱）, firework（烟花）, media_interview（媒体采访）, pet_lion（宠物狮子）, pet_chef（宠物厨师）, santa_gifts（圣诞礼物）, santa_hug（圣诞拥抱）, girlfriend（女朋友）, boyfriend（男朋友）, heart_gesture_1（比心1）, pet_wizard（宠物巫师）, smoke_smoke（烟雾）, thumbs_up（点赞）, instant_kid（瞬间小孩）, dollar_rain（美元雨）, cry_cry（哭泣）, building_collapse（建筑倒塌）, gun_shot（枪击）, mushroom（蘑菇）, double_gun（双枪）, pet_warrior（宠物战士）, lightning_power（闪电力量）, jesus_hug（耶稣拥抱）, shark_alert（鲨鱼警报）, long_hair（长发）, lie_flat（躺平）, polar_bear_hug（北极熊拥抱）, brown_bear_hug（棕熊拥抱）, jazz_jazz（爵士）, office_escape_plow（办公室逃脱）, fly_fly（飞行）, watermelon_bomb（西瓜炸弹）, pet_dance（宠物舞蹈）, boss_coming（老板来了）, wool_curly（羊毛卷）, pet_bee（宠物蜜蜂）, marry_me（嫁给我）, swing_swing（秋千）, day_to_night（日转夜）, piggy_morph（小猪变形）, wig_out（假发）, car_explosion（汽车爆炸）, ski_ski（滑雪）, tiger_hug（老虎拥抱）, siblings（兄弟姐妹）, construction_worker（建筑工人）, let's_ride（骑行）, snatched（抢夺）, magic_broom（魔法扫帚）, felt_felt（毛毡）, jumpdrop（跳落）, celebration（庆祝）, splashsplash（飞溅）, surfsurf（冲浪）, fairy_wing（仙女翅膀）, angel_wing（天使翅膀）, dark_wing（黑暗翅膀）, skateskate（滑板）, plushcut（毛绒切割）, jelly_press（果冻挤压）, jelly_slice（果冻切片）, jelly_squish（果冻挤压）, jelly_jiggle（果冻抖动）, pixelpixel（像素）, yearbook（年鉴）, instant_film（即时胶片）, anime_figure（动漫人物）, rocketrocket（火箭）, bloombloom（绽放）, dizzydizzy（眩晕）, fuzzyfuzzy（模糊）, squish（挤压）, expansion（扩张）, hug（拥抱）, kiss（亲吻）, heart_gesture（比心）, fight（打斗）等
   */
  effect_scene: string;
  /**
   * 输入对象，必填参数，包含图像URL列表和视频时长
   */
  input: {
    /**
     * 图像URL列表，必填参数，每个URL必须是有效的HTTP或HTTPS地址
     */
    images: string[];
    /**
     * 视频时长（秒），必填参数，必须为正数
     */
    duration: number;
  };
  /**
   * 外部任务ID，可选参数，用于关联外部系统的任务
   */
  external_task_id?: string;
}

export interface KlingEffectsSingleResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * kling_effects_single
 *
 * Kling AI 单人视频特效生成接口，支持棒球、内心独白、记忆复活、蹦床、猜猜看等100多种单人互动特效场景。
 * 
 * Model: kling-effects-single
 * Features:
 * - 100多种单人互动特效场景
 * - 支持人物与虚拟元素互动
 * - 自定义视频时长
 * - 高质量特效渲染
 * 
 * API Documentation: https://app.klingai.com/cn/dev/document-api/apiReference/model/videoEffects
 *
 * @example
 * ```typescript
 * import { initSeacloud, klingEffectsSingle } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await klingEffectsSingle(
 *   {
 *     "effect_scene": "hug",
 *     "input": {
 *       "images": [
 *         "https://example.com/person.jpg"
 *       ],
 *       "duration": 5
 *     }
 *   }
 * );
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 KlingEffectsSingleParams 接口定义
 * @returns 生成的资源列表
 */
export async function klingEffectsSingle(
  params: KlingEffectsSingleParams,
): Promise<KlingEffectsSingleResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'kling_effects_single',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: KlingEffectsSingleResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
